import { useCallback, useEffect, useRef } from 'react';
import { durationVital } from 'src/common/telemetry/DataDog/datadogRumApi';
import { DurationVital, DurationVitalStatus } from 'src/types/datadogRumApi';

// Custom DataDog RUM duration vital: time from dashboard mount (didMount) to the
// first Luzmo `load` event. corp context (corpId / corpName) is attached globally
// in useInitializeDatadogRum, so it rides along on this vital automatically.
export const BUSINESS_INSIGHTS_DASHBOARD_LOAD_VITAL_NAME =
  'rum.app.cmui.business_insights.dashboard_load_duration';

/**
 * Measures how long the Luzmo dashboard takes to load and reports it to DataDog.
 *
 * - Timer starts on mount and stops on the first `load` event.
 * - Subsequent `load` events (triggered when filters are re-applied) are ignored.
 * - If the user leaves before the first `load`, the vital is never stopped and
 *   DataDog does not record it — so abandoned page views are excluded by design.
 *
 * Returns a `load` handler that wraps the caller's `onLoaded`.
 */
export function useLuzmoLoadDuration(onLoaded: () => void): () => void {
  const startedRef = useRef(false);
  const stoppedRef = useRef(false);
  const vitalRef = useRef<DurationVital>();

  const onLoadedRef = useRef(onLoaded);
  useEffect(() => {
    onLoadedRef.current = onLoaded;
  }, [onLoaded]);

  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;
    vitalRef.current = durationVital(
      BUSINESS_INSIGHTS_DASHBOARD_LOAD_VITAL_NAME
    );
    vitalRef.current?.start({
      description:
        'Time from Business Admin Insights dashboard mount to the first Luzmo load event',
    });
    // No cleanup: an unstopped vital is intentionally never recorded.
  }, []);

  return useCallback((): void => {
    if (!stoppedRef.current) {
      stoppedRef.current = true;
      vitalRef.current?.stop({
        options: { context: { status: DurationVitalStatus.SUCCESS } },
      });
    }
    onLoadedRef.current();
  }, []);
}
